import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Tag, message, Input, Select, Card, Row, Col, Statistic, Popconfirm, Badge, Tooltip } from 'antd';
import { 
  ReloadOutlined, 
  SendOutlined, 
  DeleteOutlined,
  MobileOutlined,
  AppleOutlined,
  AndroidOutlined,
  DesktopOutlined,
  CheckCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { IUserDeviceToken, IDeviceTokenStats } from '../../common/notification.interface';
import NotificationApi from '../../api/notification/notification.api';
import moment from 'moment';
import './device-tokens.styles.scss';

const { Search } = Input;
const { Option } = Select;

interface DeviceTokensPageState {
  tokens: IUserDeviceToken[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  searchText: string;
  statusFilter: string;
  deviceTypeFilter: string;
  stats: IDeviceTokenStats | null;
}

const DeviceTokensPage: React.FC = () => {
  const [state, setState] = useState<DeviceTokensPageState>({
    tokens: [],
    loading: false,
    currentPage: 1,
    pageSize: 20,
    total: 0,
    searchText: '',
    statusFilter: '',
    deviceTypeFilter: '',
    stats: null,
  });

  // Load device tokens data
  const loadDeviceTokens = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const params = {
        page: state.currentPage,
        limit: state.pageSize,
        search: state.searchText || undefined,
        isActive: state.statusFilter === 'active' ? true : state.statusFilter === 'inactive' ? false : undefined,
        deviceType: state.deviceTypeFilter || undefined,
      };

      NotificationApi.getDeviceTokens(params).subscribe({
        next: (response: any) => {
          setState(prev => ({
            ...prev,
            tokens: response || [],
            total: response?.length || 0,
            loading: false,
          }));
        },
        error: (error) => {
          console.error('Error loading device tokens:', error);
          message.error('Không thể tải danh sách device tokens');
          setState(prev => ({ ...prev, loading: false }));
        }
      });
    } catch (error) {
      console.error('Error loading device tokens:', error);
      message.error('Có lỗi xảy ra khi tải dữ liệu');
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [state.currentPage, state.pageSize, state.searchText, state.statusFilter, state.deviceTypeFilter]);

  // Load device token statistics
  const loadStats = async () => {
    try {
      NotificationApi.getDeviceTokenStats().subscribe({
        next: (stats) => {
          setState(prev => ({ ...prev, stats }));
        },
        error: (error) => {
          console.error('Error loading device token stats:', error);
        }
      });
    } catch (error) {
      console.error('Error loading device token stats:', error);
    }
  };

  useEffect(() => {
    loadDeviceTokens();
    loadStats();
  }, [loadDeviceTokens]);

  // Handle table pagination
  const handleTableChange = (pagination: any) => {
    setState(prev => ({
      ...prev,
      currentPage: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  // Handle search
  const handleSearch = (value: string) => {
    setState(prev => ({
      ...prev,
      searchText: value,
      currentPage: 1,
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key: string, value: any) => {
    setState(prev => ({
      ...prev,
      [key]: value,
      currentPage: 1,
    }));
  };

  // Handle test device token
  const handleTestToken = async (tokenId: string) => {
    try {
      NotificationApi.testDeviceToken(tokenId).subscribe({
        next: (response) => {
          message.success('Gửi test notification thành công');
        },
        error: (error) => {
          console.error('Error testing device token:', error);
          message.error('Không thể gửi test notification');
        }
      });
    } catch (error) {
      console.error('Error testing device token:', error);
      message.error('Có lỗi xảy ra khi test device token');
    }
  };

  // Handle cleanup inactive tokens
  const handleCleanupTokens = async () => {
    try {
      NotificationApi.cleanupInactiveTokens(30).subscribe({
        next: (response) => {
          message.success('Dọn dẹp device tokens không hoạt động thành công');
          loadDeviceTokens();
          loadStats();
        },
        error: (error) => {
          console.error('Error cleaning up tokens:', error);
          message.error('Không thể dọn dẹp device tokens');
        }
      });
    } catch (error) {
      console.error('Error cleaning up tokens:', error);
      message.error('Có lỗi xảy ra khi dọn dẹp device tokens');
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    setState(prev => ({
      ...prev,
      searchText: '',
      statusFilter: '',
      deviceTypeFilter: '',
      currentPage: 1,
    }));
  };

  // Get device type icon
  const getDeviceTypeIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'IOS':
        return <AppleOutlined style={{ color: '#007AFF' }} />;
      case 'ANDROID':
        return <AndroidOutlined style={{ color: '#3DDC84' }} />;
      case 'WEB':
        return <DesktopOutlined style={{ color: '#1890ff' }} />;
      default:
        return <MobileOutlined />;
    }
  };

  // Get device type text
  const getDeviceTypeText = (deviceType: string) => {
    switch (deviceType) {
      case 'IOS':
        return 'iOS';
      case 'ANDROID':
        return 'Android';
      case 'WEB':
        return 'Web';
      default:
        return deviceType;
    }
  };

  // Get device type color
  const getDeviceTypeColor = (deviceType: string) => {
    switch (deviceType) {
      case 'IOS':
        return 'blue';
      case 'ANDROID':
        return 'green';
      case 'WEB':
        return 'orange';
      default:
        return 'default';
    }
  };

  // Table columns
  const columns: ColumnsType<IUserDeviceToken> = [
    {
      title: 'Người dùng',
      key: 'user',
      width: 200,
      render: (_, record: IUserDeviceToken) => (
        <div>
          <div className="user-info">
            <strong>ID: {record.userId}</strong>
          </div>
          {record.user && (
            <div className="user-details">
              <div>{record.user.username}</div>
              {(record.user.firstname || record.user.lastname) && (
                <div className="user-name">
                  {record.user.firstname} {record.user.lastname}
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Thiết bị',
      key: 'device',
      width: 150,
      render: (_, record: IUserDeviceToken) => (
        <div className="device-info">
          <div>
            {getDeviceTypeIcon(record.deviceType)}
            <Tag color={getDeviceTypeColor(record.deviceType)} style={{ marginLeft: 8 }}>
              {getDeviceTypeText(record.deviceType)}
            </Tag>
          </div>
          <div className="app-version">v{record.appVersion}</div>
        </div>
      ),
    },
    {
      title: 'Device Token',
      dataIndex: 'deviceToken',
      key: 'deviceToken',
      width: 300,
      render: (token: string) => (
        <Tooltip title={token}>
          <div className="device-token">
            {token.substring(0, 20)}...{token.substring(token.length - 20)}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag
          icon={isActive ? <CheckCircleOutlined /> : <StopOutlined />}
          color={isActive ? 'success' : 'error'}
        >
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      ),
    },
    {
      title: 'Thời gian',
      key: 'time',
      width: 180,
      render: (_, record: IUserDeviceToken) => (
        <div>
          <div>Tạo: {moment(record.createdAt).format('DD/MM/YYYY HH:mm')}</div>
          <div>Dùng cuối: {moment(record.lastUsed).format('DD/MM/YYYY HH:mm')}</div>
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: IUserDeviceToken) => (
        <Space size="small">
          <Tooltip title="Gửi test notification">
            <Button
              icon={<SendOutlined />}
              size="small"
              type="primary"
              onClick={() => handleTestToken(record.id)}
              disabled={!record.isActive}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="device-tokens-page">
      {/* Statistics Cards */}
      {state.stats && (
        <Row gutter={16} className="stats-cards">
          <Col span={6}>
            <Card>
              <Statistic
                title="Tổng device tokens"
                value={state.stats.totalTokens}
                prefix={<MobileOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Đang hoạt động"
                value={state.stats.activeTokens}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Không hoạt động"
                value={state.stats.inactiveTokens}
                prefix={<StopOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Mới gần đây"
                value={state.stats.recentTokens}
                prefix={<Badge status="processing" />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Device Type Distribution */}
      {state.stats && (
        <Card className="device-type-stats" title="Phân bố theo loại thiết bị">
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Android"
                value={state.stats.tokensByType.ANDROID}
                prefix={<AndroidOutlined style={{ color: '#3DDC84' }} />}
                valueStyle={{ color: '#3DDC84' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="iOS"
                value={state.stats.tokensByType.IOS}
                prefix={<AppleOutlined style={{ color: '#007AFF' }} />}
                valueStyle={{ color: '#007AFF' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Web"
                value={state.stats.tokensByType.WEB}
                prefix={<DesktopOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Filters */}
      <Card className="filters-card">
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Search
              placeholder="Tìm kiếm theo user ID, username..."
              allowClear
              value={state.searchText}
              onSearch={handleSearch}
              onChange={(e) => setState(prev => ({ ...prev, searchText: e.target.value }))}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Trạng thái"
              allowClear
              style={{ width: '100%' }}
              value={state.statusFilter}
              onChange={(value) => handleFilterChange('statusFilter', value)}
            >
              <Option value="active">Hoạt động</Option>
              <Option value="inactive">Không hoạt động</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Loại thiết bị"
              allowClear
              style={{ width: '100%' }}
              value={state.deviceTypeFilter}
              onChange={(value) => handleFilterChange('deviceTypeFilter', value)}
            >
              <Option value="ANDROID">Android</Option>
              <Option value="IOS">iOS</Option>
              <Option value="WEB">Web</Option>
            </Select>
          </Col>
          <Col span={10}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadDeviceTokens}
                title="Tải lại"
              />
              <Button
                onClick={handleResetFilters}
                title="Xóa bộ lọc"
              >
                Xóa bộ lọc
              </Button>
              <Popconfirm
                title="Bạn có chắc muốn dọn dẹp các device tokens không hoạt động trong 30 ngày qua?"
                onConfirm={handleCleanupTokens}
                okText="Dọn dẹp"
                cancelText="Hủy"
              >
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  title="Dọn dẹp tokens không hoạt động"
                >
                  Dọn dẹp tokens cũ
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Action Bar */}
      <Card className="action-bar">
        <Row justify="space-between" align="middle">
          <Col>
            <h2>Quản lý Device Tokens</h2>
            <p>Quản lý các device tokens để gửi push notifications</p>
          </Col>
        </Row>
      </Card>

      {/* Device Tokens Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={state.tokens}
          loading={state.loading}
          rowKey="id"
          pagination={{
            current: state.currentPage,
            pageSize: state.pageSize,
            total: state.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} device tokens`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default DeviceTokensPage;
